import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiDroplet, FiInfo } from 'react-icons/fi';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import ProcessingLoader from '../components/ProcessingLoader';
import ResultDisplay from '../components/ResultDisplay';
import './ToolPage.css';

const WatermarkRemoval = () => {
  const [file, setFile] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setOriginalPreview(selectedFile ? URL.createObjectURL(selectedFile) : null);
    setProcessedImage(null);
    setError(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/watermark-removal', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      const imageUrl = URL.createObjectURL(response.data);
      setProcessedImage(imageUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = `watermark-removed-${Date.now()}.png`;
    link.click();
  };

  const handleReset = () => {
    setFile(null);
    setOriginalPreview(null);
    setProcessedImage(null);
    setError(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Removing watermark..."
          subMessage="Our AI is analyzing and removing watermarks from your image"
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-primary">
              <FiDroplet />
            </div>
            <h1>Watermark Removal</h1>
            <p>Remove unwanted watermarks from your images with our advanced AI technology. 
               Get clean, professional results in seconds.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!processedImage ? (
            <motion.div 
              className="tool-upload-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ImageUploader 
                onFileSelect={handleFileSelect}
                title="Upload image with watermark"
                subtitle="Supports JPG, PNG, WebP formats"
              />

              {file && (
                <motion.div 
                  className="process-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button 
                    className="btn-process"
                    onClick={handleProcess}
                    disabled={isProcessing}
                  >
                    Remove Watermark
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <ResultDisplay
              originalImage={originalPreview}
              processedImage={processedImage}
              onDownload={handleDownload}
              onReset={handleReset}
            />
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>How it works</h3>
              <p>Our AI analyzes your image to detect and remove watermarks while preserving the original image quality. 
                 The process works best with semi-transparent watermarks and text overlays.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatermarkRemoval;
